import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  createEmailBlock,
  deleteEmailBlock,
  getEmailTemplate,
  listEmailTemplates,
  updateEmailTemplate,
} from "../../api/emailTemplates/emailTemplates";
import { IEmailTemplate, IEmailTemplateBlock } from "../../types/api";
import Dropdown from "../../components/UI/Dropdown/Dropdown";
import HandleConfirmationPopup from "../../containers/HandleConfirmation/HandleConfirmationPopup";
import {
  ALL_COACH_MODES,
  BLOCK_CONTEXT,
  HIDDEN_BLOCKS,
  IPreviewOptions,
  TEMPLATE_CONTROLS,
  buildPreview,
} from "./emailPreviews";
import "./AdminEmailTemplates.scss";

/**
 * Which group each template belongs to, and the order the groups appear in.
 * Keyed on the template name rather than its label, so renaming a label cannot
 * silently drop a template out of its group. Anything not listed here falls
 * into Övrigt, so a template added later still appears in the list.
 */
const TEMPLATE_GROUPS: { label: string; names: string[] }[] = [
  {
    label: "Bokningar",
    names: [
      "booking_confirmation",
      "co_player_booking_confirmation",
      "coach_booking_confirmation",
      "activity_confirmation",
      "rehab_confirmation",
    ],
  },
  {
    label: "Avbokningar",
    names: [
      "booking_cancellation",
      "co_player_cancellation",
      "coach_booking_cancellation",
      "activity_override_cancellation",
      "activity_slot_cancelled",
      "unbooked_from_activity",
      "late_activity_cancellation_office",
      "rehab_cancellation",
    ],
  },
  {
    label: "Ändringar av bokningar",
    names: [
      "booking_updated",
      "booking_moved",
      "booking_moved_co_player",
      "booking_moved_coach",
      "removed_co_player",
    ],
  },
  {
    label: "No-show",
    names: [
      "noshow_court_first",
      "noshow_court_second",
      "noshow_activity_first",
      "noshow_activity_second",
      "noshow_office_notice",
    ],
  },
  {
    label: "Välkomstmail",
    names: [
      "member_temporary_password",
      "coach_temporary_password",
      "admin_temporary_password",
    ],
  },
  {
    label: "Övrigt",
    names: ["queue_court_available", "queue_activity_available"],
  },
];

function AdminEmailTemplates() {
  const [templates, setTemplates] = useState<IEmailTemplate[]>([]);

  /**
   * Grouped for the picker. Templates the map does not mention are appended to
   * Övrigt rather than dropped, so the list can never be shorter than what the
   * API returned.
   */
  const groupedTemplateOptions = useMemo(() => {
    const byName = new Map(templates.map((t) => [t.name, t]));
    const used = new Set<string>();

    const groups = TEMPLATE_GROUPS.map((group) => ({
      label: group.label,
      options: group.names
        .filter((name) => byName.has(name))
        .map((name) => {
          used.add(name);
          return { value: name, label: byName.get(name)!.label };
        }),
    })).filter((group) => group.options.length > 0);

    const ungrouped = templates
      .filter((t) => !used.has(t.name))
      .map((t) => ({ value: t.name, label: t.label }));

    if (ungrouped.length) {
      const other = groups.find((g) => g.label === "Övrigt");
      if (other) other.options.push(...ungrouped);
      else groups.push({ label: "Övrigt", options: ungrouped });
    }

    return groups;
  }, [templates]);
  const [selectedName, setSelectedName] = useState<string>();
  const [template, setTemplate] = useState<IEmailTemplate | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [previewOptions, setPreviewOptions] = useState<IPreviewOptions>({
    // The plain booking is the ordinary case and the one landed on first, so
    // it is what the preview opens on rather than the coach variant.
    coach: "none",
    withCoPlayer: false,
    withGuest: false,
    withClayCourt: false,
    dueToRain: false,
  });

  // Which field has focus, so the matching paragraph can be highlighted in
  // the preview — the link between the two columns.
  const [focusedKey, setFocusedKey] = useState<string>();

  const previewRef = useRef<HTMLIFrameElement>(null);
  const [previewHeight, setPreviewHeight] = useState<number>();

  const resizePreview = useCallback(() => {
    const body = previewRef.current?.contentDocument?.body;
    // Four pixels of slack: scrollHeight rounds down, so measuring it exactly
    // leaves a hairline scrollbar, and two was not quite enough to clear it.
    if (body) setPreviewHeight(body.scrollHeight + 4);
  }, []);

  useEffect(() => {
    (async () => {
      const all = await listEmailTemplates();
      setTemplates(all);
      setSelectedName(all[0]?.name);
      setLoading(false);
    })();
  }, []);

  // Reload the selected template rather than reusing the list payload, so an
  // edit saved elsewhere is not shown stale.
  useEffect(() => {
    if (!selectedName) return;
    let cancelled = false;
    (async () => {
      const res = await getEmailTemplate(selectedName);
      if (cancelled || !res) return;
      setTemplate(res);
      setValues(
        [...res.blocks, ...res.custom_blocks].reduce(
          (acc, b) => ({ ...acc, [b.key]: b.value }),
          {} as Record<string, string>
        )
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedName]);

  /**
   * Keep the coach mode to one the selected template actually offers.
   *
   * The emails addressed to the coach drop "Vanlig bokning" from their toggle
   * set, so a mode carried over from the previous template can be one this one
   * does not list — which left no pill looking selected while the preview
   * rendered a variant that email never sends. Now the default is the plain
   * booking, that mismatch would be the state every one of those templates
   * opened in.
   */
  useEffect(() => {
    if (!selectedName) return;
    const modes = TEMPLATE_CONTROLS[selectedName]?.coachModes ?? ALL_COACH_MODES;
    if (!modes.length) return;
    setPreviewOptions((prev) =>
      modes.some(([mode]) => mode === prev.coach)
        ? prev
        : { ...prev, coach: modes[0][0] }
    );
  }, [selectedName]);

  const customBlocks = useMemo(
    () => template?.custom_blocks ?? [],
    [template]
  );
  const customKeys = useMemo(
    () => customBlocks.map((b) => b.key),
    [customBlocks]
  );

  const previewHtml = useMemo(
    () =>
      buildPreview(selectedName, values, previewOptions, focusedKey, customKeys),
    [selectedName, values, previewOptions, focusedKey, customKeys]
  );

  /**
   * block key -> every template that uses it, derived from the list payload so
   * no extra query is needed.
   *
   * The backend's `shared` flag only says *that* a block is shared. Knowing
   * which emails it lands in is what makes the warning actionable — otherwise
   * an admin editing the no-show paragraph has no way to find out it also goes
   * out with the medspelare confirmation and the avbokning.
   */
  const sharedWith = useMemo(() => {
    const map: Record<string, { name: string; label: string }[]> = {};
    templates.forEach((t) =>
      t.blocks.forEach((b) => {
        if (!map[b.key]) map[b.key] = [];
        map[b.key].push({ name: t.name, label: t.label });
      })
    );
    return map;
  }, [templates]);

  const hasUnsavedChanges = useMemo(
    () =>
      !!template &&
      [...template.blocks, ...template.custom_blocks].some(
        (b) => (values[b.key] ?? b.value) !== b.value
      ),
    [template, values]
  );

  /**
   * Switching template throws away unsaved edits, and the shared-block links
   * make that a much easier accident than the dropdown alone did. The chosen
   * template is parked rather than applied until the user confirms; the
   * dropdown is fully controlled, so it snaps back on its own if they cancel.
   */
  const [pendingTemplate, setPendingTemplate] = useState<string>();

  const selectTemplate = useCallback(
    (name: string) => {
      if (name === selectedName) return;
      if (hasUnsavedChanges) {
        setPendingTemplate(name);
        return;
      }
      setSelectedName(name);
    },
    [selectedName, hasUnsavedChanges]
  );

  /**
   * Covers reload, tab close and navigating away from the site. In-app route
   * changes are not caught by this — the app mounts a plain <BrowserRouter>,
   * and useBlocker only works under a data router.
   */
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Required by older browsers; the message itself is always ignored.
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [hasUnsavedChanges]);

  // srcDoc changes re-render the document without firing onLoad in every
  // browser, so measure again whenever the content could have changed.
  useEffect(() => {
    resizePreview();
    // The webfont loads after first paint and changes the height.
    const timeout = setTimeout(resizePreview, 300);
    return () => clearTimeout(timeout);
  }, [previewHtml, resizePreview]);

  const controls = selectedName ? TEMPLATE_CONTROLS[selectedName] : undefined;
  const hasControls = !!(
    controls?.clay ||
    controls?.rain ||
    controls?.participants ||
    controls?.coach
  );

  const visibleBlocks = (template?.blocks ?? []).filter(
    (block) => !HIDDEN_BLOCKS.has(block.key)
  );

  const handleSave = async () => {
    if (!template) return;
    setSaving(true);
    const blocks = [...template.blocks, ...template.custom_blocks].map((b) => ({
      key: b.key,
      value: values[b.key] ?? b.value,
    }));
    const saved = await updateEmailTemplate(template.name, blocks);
    // Move the baseline to what was just written. Without this the page stays
    // permanently "dirty" after the first save — the button never re-disables
    // and the leave guards keep firing over changes that are already stored.
    if (saved)
      setTemplate({
        ...template,
        blocks: template.blocks.map((b) => ({
          ...b,
          value: values[b.key] ?? b.value,
        })),
        custom_blocks: template.custom_blocks.map((b) => ({
          ...b,
          value: values[b.key] ?? b.value,
        })),
      });
    setSaving(false);
  };

  /**
   * Custom paragraphs. Creating and deleting one goes straight to the backend
   * — a block is a thing the email has, not a value in it — while the text
   * inside it is saved with Spara like every other block.
   */
  const [addingBlock, setAddingBlock] = useState(false);
  const [newBlockLabel, setNewBlockLabel] = useState("");
  const [creatingBlock, setCreatingBlock] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<IEmailTemplateBlock>();

  const handleCreateBlock = async () => {
    if (!template) return;
    const label = newBlockLabel.trim();
    if (!label) return;
    setCreatingBlock(true);
    const res = await createEmailBlock(template.name, label);
    if (res) {
      // Only the new key is added to values, so edits already typed into the
      // other fields — and the unsaved-changes state — survive.
      const added = res.custom_blocks.find(
        (b) => !template.custom_blocks.some((c) => c.key === b.key)
      );
      setTemplate({ ...template, custom_blocks: res.custom_blocks });
      if (added) {
        setValues((prev) => ({ ...prev, [added.key]: added.value }));
        setFocusedKey(added.key);
      }
      setNewBlockLabel("");
      setAddingBlock(false);
    }
    setCreatingBlock(false);
  };

  const handleDeleteBlock = async () => {
    if (!template || !pendingDelete) return;
    const { key } = pendingDelete;
    setPendingDelete(undefined);
    const res = await deleteEmailBlock(template.name, key);
    if (!res) return;
    setTemplate({ ...template, custom_blocks: res.custom_blocks });
    setValues((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    if (focusedKey === key) setFocusedKey(undefined);
  };

  /** A labelled on/off switch, in the site-wide toggle markup. */
  const toggle = (
    label: string,
    checked: boolean,
    onChange: (value: boolean) => void
  ) => (
    <>
      <span className="text-s secondary-text">{label}</span>
      <label className="toggle text-m">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="slider round"></span>
      </label>
    </>
  );

  return (
    <div className="admin-email-templates-layout">
      <h1 className="text-l primary-text">E-postmallar</h1>

      {loading ? (
        <div className="text-m">Laddar…</div>
      ) : templates.length === 0 ? (
        <div className="text-m">Inga mallar hittades.</div>
      ) : (
        <>
          {/* One row: which email on the left, which variant of it on the
              right. Both choose what the preview shows, so they belong
              together rather than at opposite ends of the page. */}
          <div className="template-toolbar">
            <Dropdown
              classes="template-select"
              width={320}
              height={35}
              bordered
              isSearchable
              title="Välj mall"
              value={selectedName ?? ""}
              onSelect={(value) => {
                if (value) selectTemplate(value);
              }}
              groupedOptions={groupedTemplateOptions}
            />

            {hasControls && (
              <div className="preview-mode-toggle">
                <div className="preview-extras">
                  {controls?.clay &&
                    toggle("Grusbana", previewOptions.withClayCourt, (v) =>
                      setPreviewOptions((prev) => ({
                        ...prev,
                        withClayCourt: v,
                      }))
                    )}
                  {controls?.rain &&
                    toggle("Pga regn", previewOptions.dueToRain, (v) =>
                      setPreviewOptions((prev) => ({ ...prev, dueToRain: v }))
                    )}
                  {controls?.participants && (
                    <>
                      {previewOptions.coach !== "hitting_partner" &&
                        toggle("Medspelare", previewOptions.withCoPlayer, (v) =>
                          setPreviewOptions((prev) => ({
                            ...prev,
                            withCoPlayer: v,
                          }))
                        )}
                      {controls.guests !== false &&
                        toggle("Gäst", previewOptions.withGuest, (v) =>
                          setPreviewOptions((prev) => ({
                            ...prev,
                            withGuest: v,
                          }))
                        )}
                    </>
                  )}
                </div>
                {controls?.coach &&
                  (controls.coachModes ?? ALL_COACH_MODES).map(
                    ([mode, label]) => (
                      <button
                        key={mode}
                        type="button"
                        className={`toggle-button text-m ${
                          previewOptions.coach === mode ? "active" : ""
                        }`}
                        onClick={() =>
                          setPreviewOptions((prev) => ({ ...prev, coach: mode }))
                        }
                      >
                        {label}
                      </button>
                    )
                  )}
              </div>
            )}
          </div>

          {!template ? (
            <div className="text-m">Laddar mall…</div>
          ) : (
            <div className="email-template-grid">
              <div className="email-template-editor">
                {visibleBlocks.length === 0 && customBlocks.length === 0 ? (
                  <p className="text-m secondary-text">
                    Det här utskicket har ingen redigerbar text — allt innehåll
                    är bokningsdata och den gemensamma avslutningen.
                  </p>
                ) : (
                  visibleBlocks.map((block) => {
                    const context = BLOCK_CONTEXT[block.key];
                    // Not "disabled" — the copy is still editable, it just is
                    // not part of the variant currently on screen.
                    const inPreview =
                      !context || context.appliesTo(previewOptions);
                    const others = (sharedWith[block.key] ?? []).filter(
                      (t) => t.name !== template.name
                    );
                    return (
                      <div
                        key={block.key}
                        className={`email-template-block ${
                          inPreview ? "" : "not-in-preview"
                        }`}
                      >
                        {/* Title left, reach right — the two answer different
                            questions and reading both on one line is quicker
                            than stacking them. */}
                        <div className="block-header">
                          <label
                            htmlFor={block.key}
                            className="text-m primary-text"
                          >
                            {block.label}
                          </label>
                          {others.length > 0 && (
                            <span className="text-s shared-flag">
                              Ändras även i{" "}
                              {others.map((other, index) => (
                                <Fragment key={other.name}>
                                  {index > 0 &&
                                    (index === others.length - 1
                                      ? " och "
                                      : ", ")}
                                  <button
                                    type="button"
                                    className="shared-link"
                                    onClick={() => selectTemplate(other.name)}
                                  >
                                    {other.label}
                                  </button>
                                </Fragment>
                              ))}
                            </span>
                          )}
                        </div>
                        {context?.hint && (
                          <span className="text-s secondary-text block-hint">
                            {context.hint}
                          </span>
                        )}
                        <textarea
                          id={block.key}
                          className="text-m"
                          rows={4}
                          value={values[block.key] ?? ""}
                          onFocus={() => setFocusedKey(block.key)}
                          onBlur={() => setFocusedKey(undefined)}
                          onChange={(e) =>
                            setValues((prev) => ({
                              ...prev,
                              [block.key]: e.target.value,
                            }))
                          }
                        />
                      </div>
                    );
                  })
                )}

                {customBlocks.map((block) => (
                  <div
                    key={block.key}
                    className="email-template-block custom-block"
                  >
                    <div className="block-header">
                      <label
                        htmlFor={block.key}
                        className="text-m primary-text"
                      >
                        {block.label}
                      </label>
                      <button
                        type="button"
                        className="deleteButton text-s"
                        onClick={() => setPendingDelete(block)}
                      >
                        Ta bort
                      </button>
                    </div>
                    <span className="text-s secondary-text block-hint">
                      Eget stycke – visas sist i mailet, före avslutningen
                    </span>
                    <textarea
                      id={block.key}
                      className="text-m"
                      rows={4}
                      value={values[block.key] ?? ""}
                      onFocus={() => setFocusedKey(block.key)}
                      onBlur={() => setFocusedKey(undefined)}
                      onChange={(e) =>
                        setValues((prev) => ({
                          ...prev,
                          [block.key]: e.target.value,
                        }))
                      }
                    />
                  </div>
                ))}

                {template.supports_custom_blocks &&
                  (addingBlock ? (
                    <form
                      className="add-block-form"
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleCreateBlock();
                      }}
                    >
                      <input
                        className="text-m"
                        autoFocus
                        placeholder="Rubrik på stycket, t.ex. Parkering"
                        value={newBlockLabel}
                        onChange={(e) => setNewBlockLabel(e.target.value)}
                      />
                      <button
                        type="submit"
                        className="text-m"
                        disabled={creatingBlock || !newBlockLabel.trim()}
                      >
                        {creatingBlock ? "Lägger till…" : "Lägg till"}
                      </button>
                      <button
                        type="button"
                        className="toggle-button text-m"
                        onClick={() => {
                          setAddingBlock(false);
                          setNewBlockLabel("");
                        }}
                      >
                        Avbryt
                      </button>
                    </form>
                  ) : (
                    <button
                      type="button"
                      className="add-block-button text-m"
                      onClick={() => setAddingBlock(true)}
                    >
                      + Lägg till stycke
                    </button>
                  ))}

                {(visibleBlocks.length > 0 || customBlocks.length > 0) && (
                  <div className="email-template-actions">
                    <button
                      type="button"
                      className="text-m"
                      disabled={saving || !hasUnsavedChanges}
                      onClick={handleSave}
                    >
                      {saving ? "Sparar…" : "Spara"}
                    </button>
                  </div>
                )}
              </div>

              <div className="email-template-preview">
                <iframe
                  ref={previewRef}
                  className="preview-frame"
                  title="E-post förhandsgranskning"
                  srcDoc={previewHtml}
                  style={{
                    height: previewHeight ? `${previewHeight}px` : undefined,
                  }}
                  onLoad={resizePreview}
                />
              </div>
            </div>
          )}
        </>
      )}

      <HandleConfirmationPopup
        showPopup={!!pendingTemplate}
        onClose={() => setPendingTemplate(undefined)}
        onConfirm={() => {
          if (pendingTemplate) setSelectedName(pendingTemplate);
          setPendingTemplate(undefined);
        }}
        leftTopElement={<span className="text-m">Osparade ändringar</span>}
        questionText="Du har ändringar som inte är sparade. Byter du mall försvinner de."
        confirmText="Byt mall ändå"
      />

      <HandleConfirmationPopup
        showPopup={!!pendingDelete}
        onClose={() => setPendingDelete(undefined)}
        onConfirm={handleDeleteBlock}
        leftTopElement={<span className="text-m">Ta bort stycke</span>}
        questionText={`Stycket ”${
          pendingDelete?.label ?? ""
        }” försvinner ur utskicket direkt, utan att du behöver spara. Vill du ta bort det?`}
        confirmText="Ta bort"
      />
    </div>
  );
}

export default AdminEmailTemplates;
